import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import type { HeatmapSlot } from '../models/types';
import { WarehouseMapApiAdapter } from '../adapters/WarehouseMapApiAdapter';

export const useWarehouseHeatmap = (warehouseId: number) => {
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new WarehouseMapApiAdapter(httpClient);

  return useQuery({
    queryKey: ['warehouse-heatmap', warehouseId],
    queryFn: () => adapter.getHeatmap(warehouseId),
    enabled: warehouseId > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useRackHeatmap = (heatmapData: HeatmapSlot[] | undefined, rackId: number) => {
  if (!heatmapData || !rackId) return null;

  const slots = heatmapData.filter((slot) => slot.rack_id === rackId);
  // Group by shelf_index, then sort by slot_index within each shelf
  const shelvesMap = new Map<number, HeatmapSlot[]>();
  for (const slot of slots) {
    if (!shelvesMap.has(slot.shelf_index)) {
      shelvesMap.set(slot.shelf_index, []);
    }
    shelvesMap.get(slot.shelf_index)!.push(slot);
  }

  const shelves = Array.from(shelvesMap.entries())
    .sort(([a], [b]) => b - a)
    .map(([shelfIndex, slots]) => ({
      shelf_index: shelfIndex,
      slots: slots.sort((a, b) => a.slot_index - b.slot_index),
    }));

  return shelves.length > 0 ? shelves : null;
};
